import type { Hole } from './Playfield';
import { HOLE_RADIUS } from './Playfield';

// `speed` is currently unused — preserved as a hook for future skim-too-fast / dwell-time tweaks.
export interface BallProbe { x: number; y: number; speed: number }
export type CaptureResult =
  | { kind: 'none' }
  | { kind: 'win'; hole: Hole }
  | { kind: 'fail'; hole: Hole };

/**
 * Area of the lens (intersection) of two circles with radii r1, r2 whose centers
 * are distance d apart. Returns 0 for d >= r1+r2, π·min(r1,r2)² for d <= |r1-r2|.
 */
export function lensArea(d: number, r1: number, r2: number): number {
  if (d >= r1 + r2) return 0;
  const rMin = Math.min(r1, r2);
  if (d <= Math.abs(r1 - r2)) return Math.PI * rMin * rMin;
  const r1Sq = r1 * r1;
  const r2Sq = r2 * r2;
  const dSq = d * d;
  const a1 = r1Sq * Math.acos((dSq + r1Sq - r2Sq) / (2 * d * r1));
  const a2 = r2Sq * Math.acos((dSq + r2Sq - r1Sq) / (2 * d * r2));
  const tri = 0.5 * Math.sqrt(
    (-d + r1 + r2) * (d + r1 - r2) * (d - r1 + r2) * (d + r1 + r2),
  );
  return a1 + a2 - tri;
}

/**
 * Largest d at which lensArea(d, ballR, holeR) >= coverage · π · ballR² —
 * equivalently, the fall-in capture radius for that coverage. Binary searches
 * the interval [max(0, holeR - ballR), holeR + ballR]; lensArea is monotonically
 * decreasing across that range, so the search converges lo upward to the
 * rightmost point still satisfying the predicate. 40 iterations narrow the
 * interval to ~1e-11 px (well below sub-pixel — overkill, but free).
 */
export function fallInThresholdDist(ballR: number, holeR: number, coverage: number): number {
  const ballArea = Math.PI * ballR * ballR;
  const targetLens = coverage * ballArea;
  // Max achievable lens is π · min(ballR, holeR)². If coverage demands more, clamp to 0.
  const maxLens = Math.PI * Math.min(ballR, holeR) ** 2;
  if (targetLens >= maxLens) return Math.max(0, holeR - ballR);
  let lo = Math.max(0, holeR - ballR);
  let hi = holeR + ballR;
  for (let i = 0; i < 40; i++) {
    const mid = 0.5 * (lo + hi);
    if (lensArea(mid, ballR, holeR) >= targetLens) lo = mid; else hi = mid;
  }
  return 0.5 * (lo + hi);
}

// Ball radius — duplicated from Ball.ts to keep this module Phaser-free
// (Ball.ts pulls in Phaser; this module runs in jsdom unit tests per CLAUDE.md).
const BALL_RADIUS = 8;

/**
 * Center-to-center distance at which the ball has covered ~42% of its own area
 * over a hole. Below this, the ball commits and falls in. Derived once at module
 * load so the per-frame check is a cheap squared-distance compare.
 *
 * Coverage chosen so FALL_IN_DIST > 8 (the gap between a ball pinned to a side
 * wall and its nearest in-row hole center): without that margin a player could
 * hug the wall and ride up indefinitely without ever falling in.
 */
export const FALL_IN_DIST = fallInThresholdDist(BALL_RADIUS, HOLE_RADIUS, 0.42);
const FALL_IN_DIST_SQ = FALL_IN_DIST * FALL_IN_DIST;

export function detectCapture(
  ball: BallProbe,
  holes: ReadonlyArray<Hole>,
  target: Hole,
  clearCells: Set<string>,
): CaptureResult {
  // Fall-in triggers once the ball's area covers ~42% of a hole.
  // Same threshold for win and fail; speed does not affect the check.
  {
    const dx = ball.x - target.x;
    const dy = ball.y - target.y;
    if (dx * dx + dy * dy <= FALL_IN_DIST_SQ) return { kind: 'win', hole: target };
  }
  for (const h of holes) {
    if (h.index === target.index) continue;
    if (clearCells.has(`${h.col},${h.row}`)) continue;
    const dx = ball.x - h.x;
    const dy = ball.y - h.y;
    if (dx * dx + dy * dy <= FALL_IN_DIST_SQ) return { kind: 'fail', hole: h };
  }
  return { kind: 'none' };
}
